import { TemplateResult, html, nothing, CSSResultGroup } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { KEYS } from '../../util/event-keys';
import { BlocksElement } from '../BlocksElement';
import '../icon/icons/chevron-down';
import { BlocksListItem } from '../list-item/list-item';
import { BlocksList } from '../list/list';
import type { UtilityNavTargetType } from './utility-nav-item.model';
import styles from './utility-nav-item.styles';

/**
 * @slot default - The utility nav content
 * @slot icon - Default (cat-icon-chevron-down>)
 * @slot custom-link - The component's custom link slot
 * @slot before - Utility nav icon before
 * @csspart base - The component's base wrapper.
 * @csspart link - The component's link wrapper.
 * @csspart text-before - The component's text-before item.
 * @csspart part-before - The component's before slot.
 * @csspart part-text - The component's text.
 * @csspart after - The component's after wrapper.
 * @csspart part-icon - The component's icon slot.
 * @csspart after-icon - The component's after-icon wrapper.
 * @csspart panel - The component's panel wrapper.
 * @csspart part-default - The component's default slot.
 * @csspart part-custom-link - The component's custom link slot
 */
export class BlocksUtilityNavItem extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Dropdown toggle on/off
   * 1) If item has dropdown, show dropdown
   */
  @property({ type: Boolean })
  dropdown: boolean | undefined;

  /**
   * Aria label attribute
   * - Provides an accessible name for the nav-link
   */
  @property({ type: String })
  arialabel: string | undefined;

  /**
   * Hide text toggle
   * 1) Visually hides the text so screenreaders can still read for accessibility when set to true.
   */
  @property({ type: Boolean })
  hideText: boolean | undefined;

  /**
   * Hide text on Mobile
   * 1) Use for hiding the nav item text at mobile view, for better clearity and space management at small device
   */
  @property({ type: Boolean })
  hideTextOnMobile: boolean | undefined;

  /**
   * URL of the utility nav item
   */
  @property({ type: String })
  href: string | undefined;

  /*
   * Target to display the linked URL.
   *_self: the current browsing context.(Default)
   *_blank: opens the linked url in new tab.
   *_parent: the parent browsing context of the current one. If no parent, behaves as _self.
   *_top: the topmost browsing context (the "highest" context that's an ancestor of the current one). If no ancestors, behaves as _self.
   */
  @property({ type: String })
  target: UtilityNavTargetType;

  /**
   * Current state added to represent the current page or parent location in product
   */
  @property({ type: Boolean })
  isCurrent: boolean | undefined;

  /**
   * Text of the utility nav item
   */
  @property({ type: String })
  text: string | undefined;

  /**
   * Append to the class name. Used for passing in utility classes
   */
  @state()
  isActive: boolean | undefined;

  /**
   * To query the utilityNavLink
   */
  @query('.cat-c-utility-nav__item.cat-is-active > .cat-c-utility-nav__link')
  private _utilityNavLink: HTMLButtonElement | HTMLAnchorElement | undefined;

  private get navItems(): BlocksUtilityNavItem[] | undefined {
    const utilityNavItem = this.parentNode?.querySelectorAll<BlocksUtilityNavItem>('cat-utility-nav-item');

    if (utilityNavItem) {
      return Array.from(utilityNavItem);
    }
  }

  private isItemSelected = false;

  /**
   * Connected Callback lifecycle
   * 1) Add event listeners
   */
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('mousedown', this.handleOnClickOutside, false);
  }

  /**
   * Disconnected callback lifecycle
   * 1) Remove event listeners
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('mousedown', this.handleOnClickOutside, false);
  }

  override focus(options?: FocusOptions) {
    this._utilityNavLink?.focus(options);
  }

  /**
   * Handle click outside the component
   * 1) Close the show hide panel on click outside
   * 2) If the nav is already closed then we don't care about outside clicks and we
   * can bail early
   * 3) By the time a user clicks on the page the shadowRoot will almost certainly be
   * defined, but TypeScript isn't that trusting and sees this.shadowRoot as possibly
   * undefined. To work around that we'll check that we have a shadowRoot (and a
   * rendered .host) element here to appease the TypeScript compiler. This should never
   * actually be shown or run for a human end user.
   * 4) Check to see if we clicked inside the active navigation item
   * 5) If the navigation is active and we've clicked outside of the nav then it should
   * be closed.
   */
  private handleOnClickOutside = (event: MouseEvent) => {
    /* 2 */
    if (!this.isActive) {
      return;
    }

    /* 3 */
    if (!this.shadowRoot?.host) {
      throw Error('Could not determine navigation context during click handler');
    }

    /* 4 */
    const didClickInside = event.composedPath().includes(this.shadowRoot.host);

    /* 5 */
    if (this.isActive && !didClickInside) {
      this.isActive = false;
      this.isItemSelected = false;
    }
  };

  /**
   * Handle keydown
   * 1) If item is clicked, navigate to the custom link
   */
  private handleCustomLinkClick(e: MouseEvent) {
    if (e.type === 'click') {
      this._children?.[0].click(); /* 1 */
    }
  }

  /**
   * Toggle active state of primary nav item
   * 1) Remove isActive and isCurrent state from all sibling elements where the current nav item is not a dropdown
   * 2) If current nav item have dropdown and isCurrent is false, retain the same for isCurrent, else make true
   * 3) Toggle active state of element selected
   */
  private clickHandler = async (e: MouseEvent) => {
    e.preventDefault();
    if (this.navItems) {
      this.navItems.forEach((element: BlocksUtilityNavItem) => {
        if (element !== this && !this.dropdown) {
          element.isCurrent = false; /*1*/
          element.isActive = false;
        } else if (this.dropdown && !this.isCurrent) {
          this.isCurrent = false; /*2*/
        } else {
          this.isCurrent = true;
        }
      });
    }
    this.isActive = !this.isActive; /* 3 */
    await this.updateComplete;
    this.focus();
    this.isItemSelected = false;
  };

  private closePanel() {
    this.isActive = false;
    this.isItemSelected = false;
  }
  /**
   * Handle keydown
   * 1) If key selected is enter, navigate to the custom link
   */
  private handleOnKeyDown(e: KeyboardEvent) {
    if (this.slotNotEmpty('custom-link')) {
      if (e.key === KEYS.ENTER) {
        this._children?.[0].click(); /* 1 */
      }
    }

    if (this.isActive === true) {
      const activeTagName = this.tagName.toUpperCase();
      if (this.dropdown && (e.key === KEYS.ESCAPE || e.key === KEYS.TAB)) {
        e.preventDefault();
        this.closePanel();
        this._utilityNavLink?.focus();
      } else if (activeTagName !== 'CAT-LIST-ITEM') {
        const catListItems = this.querySelectorAll('cat-list-item');
        if (catListItems.length > 0) {
          let lastFocusableElement;
          for (let i = catListItems.length - 1; i >= 0; i--) {
            if (!catListItems[i].shadowRoot?.querySelector('.cat-c-list-item.cat-is-disabled')) {
              lastFocusableElement = catListItems[i];
              break;
            }
          }

          if (e.key === KEYS.DOWN_ARROW && !this.isItemSelected) {
            catListItems[0].focusOnFirstItem(e);
            this.isItemSelected = true;
          } else if (e.key === KEYS.UP_ARROW && !this.isItemSelected) {
            lastFocusableElement?.focusOnLastItem(e);
            this.isItemSelected = true;
          }
        }
      }
    }
  }

  /**
   * List item selected from the primary nav item panel
   * 1) Remove isActive and isCurrent state from all sibling elements and making current nav item as isCurrent
   * 2) If the event target in the primary nav panel is the list-item or child of the list-item and
   * that doesn't contain any nested lists (e.g. expandable list item), close the panel
   * 3) If the item has children, then remove all `isCurrent` states from those items if another item is selected
   * 4) If nested children within dropdown, find all parent node list items and set `isCurrent` if that item is selected.
   */
  private itemSelected(evt: MouseEvent) {
    const subListElement = evt.target as BlocksListItem;
    if (subListElement?.disabled) {
      return;
    }
    if (this.navItems) {
      this.navItems.forEach((element: BlocksUtilityNavItem) => {
        if (element !== this) {
          element.isCurrent = false; /*1*/
        } else {
          this.isCurrent = true;
        }
      });
    }
    if (
      ((subListElement.outerHTML.includes('cat-list-item') && !subListElement.querySelector('cat-list')) ||
        (subListElement.parentElement?.outerHTML.includes('cat-list-item') && !subListElement.parentElement.querySelector('cat-list'))) &&
      !subListElement.disabled &&
      !subListElement.querySelector('cat-list')
    ) {
      this.resetAllListItems(); /* 3 */
      this.updateSelectedItem(subListElement); /* 4 */
      this.closePanel(); /* 2 */
      this._utilityNavLink?.focus();
    }
  }

  /**
   * Update selected item
   * 1) If the selected item is a child of the list-item, set the parent list-item as current
   */
  private updateSelectedItem(subListElement: BlocksListItem): void {
    let newSubListElement: BlocksListItem | null = subListElement.closest('cat-list-item');
    while (newSubListElement) {
      newSubListElement.isCurrent = true;
      newSubListElement.isActive = true;
      newSubListElement = newSubListElement.parentElement?.closest('cat-list-item') || null;
    }
  }

  /**
   * Reset all list items
   * 1) Reset all list items to false
   */
  private resetAllListItems(): void {
    const allCatUtilityNavItems = this.closest('cat-utility-nav')?.querySelectorAll('cat-utility-nav-item');
    allCatUtilityNavItems?.forEach((element: Element) => {
      const allListItems = element.querySelectorAll<BlocksListItem>('cat-list-item');
      allListItems.forEach((item) => {
        item.isCurrent = false;
        item.isActive = false;
      });
    });
  }

  private setRoles() {
    if (this.dropdown) {
      const list = this.querySelectorAll('cat-list');
      const listItems = this.querySelectorAll('cat-list-item');

      setTimeout(() => {
        list.forEach((item: BlocksList) => {
          item.listRole = 'menu';
        });
        listItems.forEach((item: BlocksListItem) => {
          item.listItemRole = 'menuitem';
        });
      }, 1);
    }
  }

  render(): TemplateResult<1> {
    const componentClassName = this.componentClassNames('cat-c-utility-nav__item', {
      'cat-is-active': this.isActive === true,
      'cat-is-current': this.isCurrent === true,
      'cat-c-utility-nav__mobile-view': this.hideTextOnMobile === true
    });

    if (this.href) {
      return html`
        <li part="base" role="none" class="${componentClassName}">
          <a
            role="menuitem"
            part="link"
            href="${this.href}"
            class="cat-c-utility-nav__link"
            aria-label=${ifDefined(this.arialabel)}
            target="${ifDefined(this.target)}"
          >
            ${this.slotNotEmpty('before')
              ? html`
                  <div
                    part="text-before"
                    class=${this.text && !this.hideText ? 'cat-c-utility-nav__icon cat-c-utility-nav__icon-before' : 'cat-c-utility-nav__icon'}
                  >
                    <slot part="part-before" name="before"></slot>
                  </div>
                `
              : html`${nothing}`}
            <span part="part-text" class="${this.hideText ? 'cat-u-is-vishidden cat-c-utility-nav__text' : 'cat-c-utility-nav__text'}"
              >${this.text}</span
            >
          </a>
        </li>
      `;
    } else if (this.slotNotEmpty('custom-link')) {
      return html`
        <li part="base" role="none" class="${componentClassName}" @keydown=${this.handleOnKeyDown}>
          <div part="link" class="cat-c-utility-nav__link" aria-label=${ifDefined(this.arialabel)} tabindex=${0} @click=${this.handleCustomLinkClick}>
            <slot part="part-custom-link" name="custom-link" tabindex=${-1}></slot>
          </div>
        </li>
      `;
    } else {
      return html`
        <li part="base" role="none" class="${componentClassName}" @keydown=${this.handleOnKeyDown}>
          <button part="link" role="menuitem" class="cat-c-utility-nav__link" @click=${this.clickHandler}>
            ${this.slotNotEmpty('before')
              ? html`
                  <div
                    part="text-before"
                    class=${this.text && !this.hideText ? 'cat-c-utility-nav__icon cat-c-utility-nav__icon-before' : 'cat-c-utility-nav__icon'}
                  >
                    <slot part="part-before" name="before"></slot>
                  </div>
                `
              : html`${nothing}`}
            <span part="part-text" class="${this.hideText ? 'cat-u-is-vishidden cat-c-utility-nav__text' : 'cat-c-utility-nav__text'}"
              >${this.text}</span
            >
            ${this.dropdown
              ? html`
                  <div part="after" class="cat-c-utility-nav__icon-after">
                    <slot part="part-icon" name="icon">
                      <cat-icon-chevron-down part="after-icon" exportparts=${this.getExportParts('chevron-down', 'icon')}></cat-icon-chevron-down>
                    </slot>
                  </div>
                `
              : html`${nothing}`}
          </button>
          ${this.dropdown
            ? html`
                <div part="panel" class="cat-c-utility-nav__item-panel">
                  <slot part="part-default" @click=${this.itemSelected} @slotchange=${this.setRoles}></slot>
                </div>
              `
            : html`${nothing}`}
        </li>
      `;
    }
  }
}

if (customElements.get('cat-utility-nav-item') === undefined) {
  customElements.define('cat-utility-nav-item', BlocksUtilityNavItem);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-utility-nav-item': BlocksUtilityNavItem;
  }
}
